import { UserContext, UserDispatchContext, useUpgradeModal } from "../../context/user";
import React, { useContext, useEffect, useState } from "react";
import "./Header.css";
import axios from "../../../libs/axios";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../../assets/LogoLatest.svg";

const Header = ({ style }) => {
  const user = useContext(UserContext);
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const navigateHeader = (navigateUrl) => {
    switch (navigateUrl) {
      case "/":
      case "/blogs":
      case "/pricing":
      case "/resources":
      case "/comparison":
      case "/about":
        navigate(navigateUrl);
        break;
      default:
        break;
    }
    window.scrollTo(0, 0);
  };

  const location = useLocation();
  const [currentLocation, setCurrentLocation] = useState(location.pathname);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDemoModalOpen, setDemoModalOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentLocation(location.pathname);
  }, [location]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDemoModal = () => {
    setDemoModalOpen(!isDemoModalOpen);

    // FIXED: Completely prevent body scrolling when modal opens/closes
    if (!isDemoModalOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  };

  const handleDemoFormChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone number field
    if (name === 'phone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');

      // Limit to 10 digits
      const limitedDigits = digitsOnly.slice(0, 10);

      setDemoForm(prev => ({
        ...prev,
        [name]: limitedDigits
      }));
    } 
    // Special handling for message field - 500 character limit
    else if (name === 'message') {
      // Limit to 500 characters
      const limitedMessage = value.slice(0, 500);

      setDemoForm(prev => ({
        ...prev,
        [name]: limitedMessage
      }));
    } 
    else {
      setDemoForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Phone number input handler to prevent non-digit input
  const handlePhoneKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right arrows
    if ([8, 9, 27, 13, 46, 35, 36, 37, 39].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress if length is already 10
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
    // Prevent input if already 10 digits
    if (demoForm.phone.length >= 10 && ![8, 46].includes(e.keyCode)) {
      e.preventDefault();
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number length
    if (demoForm.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    try {
      setLoading(true);
      await fetch(process.env.REACT_APP_GOOGLE_SCRIPT, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: demoForm.name,
          email: demoForm.email,
          phone: demoForm.phone,
          message: demoForm.message,
        }),
      });

      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 4000);

      // Reset form after successful submission
      setDemoForm({ name: "", phone: "", email: "", message: "" });

      // Close modal after submission
      toggleDemoModal();
    } catch (error) {
      console.error("Frontend error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Close mobile menu when clicking outside
  const handleOverlayClick = () => {
    setMobileMenuOpen(false);
  };

  // FIXED: Remove overlay click to close - only close with X button
  const handleModalOverlayClick = (e) => {
    // Prevent any clicks from closing the modal
    // Modal will only close via X button or Cancel button
    e.stopPropagation();
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="menu-overlay active" onClick={handleOverlayClick}></div>
      )}

      <header
        className={`main-header sm ${isMobileMenuOpen ? "mobile-menu-open" : ""}`}
        style={style}
      >
        <div className="header-container">
          {/* Logo */}
          <div className="logo-header" onClick={() => navigateHeader("/")}>
            <img src={logo} alt="Logo" />
          </div>

          {/* Menu Items */}
          <div className={`menu-items ${isMobileMenuOpen ? "mobile-menu" : ""}`}>
            <div className="menu-bar">
              <div
                className={`menu-item ${currentLocation === "/pricing" ? "active" : ""}`}
                onClick={() => navigateHeader("/pricing")}
              >
                Pricing
              </div>
              <div
                className={`menu-item ${currentLocation === "/blogs" ? "active" : ""}`}
                onClick={() => navigateHeader("/blogs")}
              >
                Blogs
              </div>
              <div
                className={`menu-item ${currentLocation === "/about" ? "active" : ""}`}
                onClick={() => navigateHeader("/about")}
              >
                About Us
              </div>

              {/* Resources Dropdown */}
              <div
                className={`menu-item ${currentLocation === "/resources" ? "active" : ""}`}
              >
                Resources
                <div className="submenu">
                  <div onClick={() => navigateHeader("/comparison")}>
                    All Comparisons
                  </div>
                  <div
                    onClick={() => (window.location.href = "https://appoyster.tawk.help/")}
                  >
                    Support Center
                  </div>
                </div>
              </div>

              {/* Get a Demo Button in Menu */}
              <div className="menu-item demo-menu-item" onClick={toggleDemoModal}>
                Get a Demo
              </div>
            </div>

            {/* Mobile Buttons */}
            <div className="mobile-menu-buttons">
              <button className="demo-btn" onClick={toggleDemoModal}>
                Get a Demo
              </button>
              <button className="login" onClick={() => handleNavigation("/auth")}>
                Log In
              </button>
              <button
                className="start"
                onClick={() => handleNavigation("/new-sign-up")}
              >
                Start for FREE
              </button>
            </div>
          </div>

          {/* Desktop Buttons */}
          {user == null ? (
            <div className="desktop-menu-buttons">
              <button className="login" onClick={() => handleNavigation("/auth")}>
                Log In
              </button>
              <button
                className="start"
                onClick={() => handleNavigation("/new-sign-up")}
              >
                Start for FREE
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className="user_profile_Pic_HOMEPAGE">
                  {user?.name?.substring(0, 1)}
                </div>
                <div style={{ marginTop: "2%" }}>
                  <div
                    style={{
                      color: "var(--gray-500, #556987)",
                      fontFamily: "Poppins",
                      fontSize: "16px",
                      fontWeight: 500,
                      lineHeight: "24px",
                    }}
                  >
                    {user?.name ?? "Anonymous"}
                  </div>
                  <div
                    style={{
                      cursor: "pointer",
                      color: "#3A3AF4",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }}
                    onClick={() => {
                      handleNavigation("/dashboard");
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = "none";
                    }}
                  >
                    Go to the Editor
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hamburger Menu / Cross */}
          <div
            className={`hamburger-menu ${isMobileMenuOpen ? "cross" : ""}`}
            onClick={toggleMobileMenu}
          >
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        </div>
      </header>

      {/* FIXED Demo Modal - Only closes with X button or Cancel button */}
      {isDemoModalOpen && (
        <div className="demo-modal-overlay" onClick={handleModalOverlayClick}>
          <div className="demo-modal">
            <div className="demo-modal-header">
              <h2>Get a Demo</h2>
              <button className="close-modal" onClick={toggleDemoModal}>
                ×
              </button>
            </div>
            <form className="demo-form" onSubmit={handleDemoSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={demoForm.name}
                  onChange={handleDemoFormChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={demoForm.phone}
                  onChange={handleDemoFormChange}
                  onKeyDown={handlePhoneKeyDown}
                  required
                  placeholder="Enter your 10-digit phone number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  title="Please enter a 10-digit phone number"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={demoForm.email}
                  onChange={handleDemoFormChange}
                  required
                  placeholder="Enter your email address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={demoForm.message}
                  onChange={handleDemoFormChange}
                  placeholder="Tell us about your requirements (optional)"
                  rows="4"
                  maxLength="500"
                ></textarea>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'right',
                  marginTop: '4px'
                }}>
                  {demoForm.message.length}/500 characters
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={toggleDemoModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Request Demo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbarOpen && (
        <div className="snackbar" style={{
          minWidth: '350px',
          maxWidth: '420px'
        }}>
          <div className="snackbar-content" style={{
            padding: '18px 22px',
            gap: '12px'
          }}>
            <div className="snackbar-message" style={{
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '1.5',
              padding: '2px 0'
            }}>
              Thank you! We will contact you soon for the demo.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;